import { useCallback, useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DownloadIcon, PrinterIcon } from 'lucide-react';
import { Panel } from '../ui/Panel';
import { LoadFailed, LoadingLine } from '../ui/LoadState';
import { btnSecondary, inputCls } from '../ui/styles';
import { useAdmin } from '../AdminContext';
import { fetchAgenda } from '../adminApi';
import { useLatestLoad } from '../../hooks/useLatestLoad';
import { daysOfMonth, hoursLabel, staffHoursCsv, staffHoursMonth } from '../../core/rules/staffHours';
import { staffList, staffName } from '../../core/rules/salonDays';
import { dayShort, today } from '../../core/format/dates';
import { downloadCsv } from '../../utils/download';
import { printStaffHours } from './staffHoursDocument';
import type { AgendaRange } from '../../core/types/agenda';
const monthLabelOf = (month: string) => {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('nl-NL', {
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Omzet & rapporten › Urenoverzicht per collega per maand (alleen Sarah): gewerkte salondagen met
 * tijden, afgeronde behandelingen, niet gekomen en geannuleerd apart, en totalen. Downloaden (CSV)
 * en printen, om naast de factuur van de collega te leggen. Geen omzet: alle omzet is van Beauty Bewust.
 */
export function StaffHoursPanel() {
  const id = useId();
  const {
    site
  } = useAdmin();
  const team = staffList(site.staff);
  const colleagues = team.filter(s => s.role === 'staff');
  const now = today();
  const [month, setMonth] = useState(now.slice(0, 7));
  const [who, setWho] = useState(colleagues[0]?.id ?? team[0].id);
  const days = daysOfMonth(month);
  const from = days[0];
  const to = days[days.length - 1];
  const load = useCallback((opts: {
    signal?: AbortSignal;
  }) => fetchAgenda(from, to, opts), [from, to]);
  const {
    data,
    status,
    reload
  } = useLatestLoad<AgendaRange>(load);
  const report = useMemo(() => data ? staffHoursMonth(site.openingHours, site.salonExceptions, data.appointments, who, month, now) : null, [data, site.openingHours, site.salonExceptions, who, month, now]);
  const name = staffName(team, who);
  const label = monthLabelOf(month);
  const print = async () => {
    if (report && !(await printStaffHours(report, name, label, site.salon.name))) toast.error('Printen lukte niet', {
      description: 'Probeer het opnieuw of gebruik een andere browser.'
    });
  };
  return <Panel title="Urenoverzicht" description="Per maand: gewerkte dagen met tijden en behandelingen, om naast de factuur van je collega te leggen." actions={<div className="flex flex-wrap gap-2" data-id="element-3947" data-mp="z6mycd7bp9gy">
          <button type="button" onClick={() => report && downloadCsv(`uren-${name.toLowerCase()}-${month}.csv`, staffHoursCsv(report, name))} disabled={!report} className={btnSecondary} data-id="element-3948" data-mp="z9slhmwe61a1">
            <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3949" data-mp="2amla4otvy9lv" />
            CSV
          </button>
          <button type="button" onClick={() => void print()} disabled={!report} className={btnSecondary} data-id="element-3950" data-mp="2eqawr8r02h49">
            <PrinterIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-3951" data-mp="3v4vtoijam1o3" />
            Printen
          </button>
        </div>} data-id="element-3946" data-mp="pb34dwcbkkuu">
      <div className="flex flex-wrap gap-4" data-id="element-3952" data-mp="3qcvz75lzs0yw">
        <label htmlFor={`${id}-who`} className="block" data-id="element-3953" data-mp="3lf3zhpf3vak5">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-3954" data-mp="3leks32n304kd">Medewerker</span>
          <select id={`${id}-who`} value={who} onChange={e => setWho(e.target.value)} className={`${inputCls} w-48`} data-id="element-3955" data-mp="3gh2fn29uv77m">
            {[...colleagues, ...team.filter(s => s.role === 'owner')].map(s => <option key={s.id} value={s.id} data-id="element-3956" data-mp="37g49oefuwpme">
                {s.name}
                {s.active ? '' : ' (gestopt)'}
              </option>)}
          </select>
        </label>
        <label htmlFor={`${id}-month`} className="block" data-id="element-3957" data-mp="15mwwbajt0i35">
          <span className="mb-1 block text-[12px] text-muted" data-id="element-3958" data-mp="10ove0ru7cmsq">Maand</span>
          <input id={`${id}-month`} type="month" value={month} max={now.slice(0, 7)} onChange={e => e.target.value && setMonth(e.target.value)} className={`${inputCls} w-48`} data-id="element-3959" data-mp="10pmwd0ysgo12" />
        </label>
      </div>

      {status === 'error' && !data ? <LoadFailed variant="inline" title="De agenda laden lukte niet" onRetry={reload} className="mt-5" data-id="element-3960" data-mp="3kj6u1e1uvr83" /> : !report ? <LoadingLine className="mt-5" data-id="element-3961" data-mp="3pgfczgutvzae" /> : <>
          {/* De losse regel staat naast de <dl>, niet erin: een <dl> mag alleen dt/dd-groepen bevatten (axe `definition-list`). */}
          <div className="mt-5 flex flex-wrap items-end gap-x-10 gap-y-3" data-id="element-3962" data-mp="3fkcf4n70emt9">
            <dl className="flex flex-wrap gap-x-10 gap-y-3" data-id="element-3963" data-mp="31syvzad0jqyr">
              <div data-id="element-3964" data-mp="31qco19m7n9g0">
                <dt className="text-[12px] text-muted" data-id="element-3965" data-mp="35thpr9ygv73l">Uren</dt>
                <dd className="font-display text-[1.8rem] font-light leading-none tabular-nums text-ink" data-id="element-3966" data-mp="35roibsg14iuj">{hoursLabel(report.totals.minutes)}</dd>
              </div>
              <div data-id="element-3967" data-mp="2ws1gj5je1b0y">
                <dt className="text-[12px] text-muted" data-id="element-3968" data-mp="2wsjmitvur3m2">Behandelingen afgerond</dt>
                <dd className="font-display text-[1.8rem] font-light leading-none tabular-nums text-ink" data-id="element-3969" data-mp="2d82jt1vi9hw6">{report.totals.done}</dd>
              </div>
            </dl>
            <p className="text-[13px] text-muted" data-id="element-3970" data-mp="2n57b1g47k3ju">
              {report.totals.days} {report.totals.days === 1 ? 'dag' : 'dagen'} · {report.totals.noShow} niet gekomen · {report.totals.cancelled} geannuleerd
            </p>
          </div>
          {report.days.length === 0 ? <p className="mt-5 text-[13px] text-muted" data-id="element-3971" data-mp="2w3sqv3iap4gx">{name} heeft in {label} (nog) niet gewerkt.</p> :
      // Scrollt horizontaal op een telefoon: met tabIndex ook met het toetsenbord te bereiken en te scrollen.
      <div className="mt-5 overflow-x-auto rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-clay" tabIndex={0} role="region" aria-label={`Uren van ${name} in ${label}`} data-id="element-3972" data-mp="30wlt9kqak0pu">
              <table className="w-full text-[13px]" data-id="element-3973" data-mp="3aww5b20lb23f">
                <caption className="sr-only" data-id="element-3974" data-mp="3hyrjjpue68k5">
                  Uren van {name} in {label}
                </caption>
                <thead className="text-left text-muted" data-id="element-3975" data-mp="3mydyc3pvbwv2">
                  <tr className="border-b border-line" data-id="element-3976" data-mp="3rwfikrz16284">
                    <th scope="col" className="py-2 pr-3 font-normal" data-id="element-3977" data-mp="3ruuof3gmolas">Dag</th>
                    <th scope="col" className="py-2 pr-3 font-normal" data-id="element-3978" data-mp="it8i1fytt6c">Tijden</th>
                    <th scope="col" className="py-2 pr-3 text-right font-normal" data-id="element-3979" data-mp="4jv0pybgt4yt">Uren</th>
                    <th scope="col" className="py-2 pr-3 text-right font-normal" data-id="element-3980" data-mp="9i69e5q0x9fg">Afgerond</th>
                    <th scope="col" className="py-2 pr-3 text-right font-normal" data-id="element-3981" data-mp="ehslgbu48jmo">Niet gekomen</th>
                    <th scope="col" className="py-2 text-right font-normal" data-id="element-3982" data-mp="jgc9chs9s5ho">Geannuleerd</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line tabular-nums text-ink" data-id="element-3983" data-mp="2g9xiu2udw94s">
                  {report.days.map(d => <tr key={d.date} data-id="element-3984" data-mp="2l88umj48otpn">
                      <td className="py-2 pr-3 capitalize" data-id="element-3985" data-mp="12cma19sb9jgv">
                        {dayShort(d.date)}
                        {d.exception && <span className="ml-1 text-[12px] normal-case text-muted" data-id="element-3986" data-mp="xlw15mkoeyhf">afwijkend</span>}
                      </td>
                      <td className="py-2 pr-3" data-id="element-3987" data-mp="tl2esxmvtrqn">
                        {d.open}–{d.close}
                      </td>
                      <td className="py-2 pr-3 text-right" data-id="element-3988" data-mp="ojd2zfru5a1i">{hoursLabel(d.minutes)}</td>
                      <td className="py-2 pr-3 text-right" data-id="element-3989" data-mp="1laxgzuvu9bwq">{d.done}</td>
                      <td className="py-2 pr-3 text-right" data-id="element-3990" data-mp="1hb6if502lrhn">{d.noShow}</td>
                      <td className="py-2 text-right" data-id="element-3991" data-mp="1casbxre4aplq">{d.cancelled}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </>}
    </Panel>;
}